'use client'

import { Box, Button, Flex, Image, Portal, Text } from '@chakra-ui/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FiExternalLink } from 'react-icons/fi'
import { isDocx, isPptx } from '@/components/Drive/DocumentPreview'
import { CloseButton } from '@/components/ui/CloseButton'
import { useApiContext } from '@/utils/api'
import type { Attachment } from '@/utils/types'

interface InvestorRelationsMobileDocumentViewerProps {
  attachment: Attachment | null
  isOpen: boolean
  onClose: () => void
  companySlug: string
  section: string
  isPreview?: boolean
  organisationId?: string
  preDownloadedBlobUrl?: string | null
  assiduousIconUrl?: string
}

const buildAbsoluteApiUrl = (
  baseUrl: string | undefined,
  pathOrUrl: string
) => {
  if (/^(https?:|blob:)/i.test(pathOrUrl)) return pathOrUrl

  const normalisedBaseUrl = String(baseUrl || '').replace(/\/$/, '')
  const normalisedPath = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`
  return `${normalisedBaseUrl}${normalisedPath}`
}

const getIRDocumentPath = ({
  attachment,
  companySlug,
  section,
  isPreview,
  organisationId,
}: {
  attachment: Attachment
  companySlug: string
  section: string
  isPreview: boolean
  organisationId?: string
}) => {
  const shouldConvertToPdf =
    isPptx(attachment.fileType) || isDocx(attachment.fileType)
  const queryString = shouldConvertToPdf ? '?convertToPdf=true' : ''

  if (isPreview) {
    if (!organisationId) return null
    return `/admin/investor-relations/${organisationId}/documents/${section}/${attachment.attachmentId}${queryString}`
  }
  if (!companySlug) return null
  return `/investor-relations/${companySlug}/documents/${section}/${attachment.attachmentId}${queryString}`
}

const InvestorRelationsMobileDocumentViewer = ({
  attachment,
  isOpen,
  onClose,
  companySlug,
  section,
  isPreview = false,
  organisationId,
  preDownloadedBlobUrl = null,
  assiduousIconUrl = '/images/AssiduousIcon.svg',
}: InvestorRelationsMobileDocumentViewerProps) => {
  const { api, anonymousApi } = useApiContext()
  const attemptedOpenRef = useRef<string | null>(null)
  const [blockedUrl, setBlockedUrl] = useState<string | null>(null)

  const documentUrl = useMemo(() => {
    if (!attachment) return null
    if (preDownloadedBlobUrl) return preDownloadedBlobUrl

    const documentPath = getIRDocumentPath({
      attachment,
      companySlug,
      section,
      isPreview,
      organisationId,
    })
    if (!documentPath) return null

    const apiBaseUrl = isPreview
      ? api.defaults.baseURL
      : anonymousApi.defaults.baseURL
    return buildAbsoluteApiUrl(apiBaseUrl, documentPath)
  }, [
    anonymousApi.defaults.baseURL,
    api.defaults.baseURL,
    attachment,
    companySlug,
    isPreview,
    organisationId,
    preDownloadedBlobUrl,
    section,
  ])

  const openDocument = useCallback(() => {
    if (!documentUrl) return false

    const openedWindow = window.open(documentUrl, '_blank')
    if (!openedWindow) {
      setBlockedUrl(documentUrl)
      return false
    }

    openedWindow.opener = null
    setBlockedUrl(null)
    onClose()
    return true
  }, [documentUrl, onClose])

  useEffect(() => {
    if (!isOpen || !documentUrl || attemptedOpenRef.current === documentUrl) {
      return
    }

    attemptedOpenRef.current = documentUrl
    openDocument()
  }, [documentUrl, isOpen, openDocument])

  useEffect(() => {
    if (!isOpen) {
      attemptedOpenRef.current = null
      setBlockedUrl(null)
    }
  }, [isOpen])

  if (!isOpen || !attachment || !blockedUrl) return null

  return (
    <Portal>
      <Box
        position="fixed"
        inset={0}
        zIndex={9999}
        bg="gray.900"
        display="flex"
        flexDirection="column"
      >
        <Flex
          h="64px"
          px={4}
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px"
          borderColor="gray.800"
          flexShrink={0}
        >
          <Image
            src={assiduousIconUrl}
            alt="Assiduous Logo"
            w="28px"
            h="28px"
            flexShrink={0}
          />
          <CloseButton
            label="Close viewer"
            onClose={onClose}
            appearance="overlay"
            position="static"
            color="gray.400"
            _hover={{ color: 'gray.100' }}
          />
        </Flex>

        <Flex
          flex={1}
          minH={0}
          align="center"
          justify="center"
          direction="column"
          gap={4}
          px={6}
          textAlign="center"
        >
          <Text color="white" fontWeight="700">
            Open document
          </Text>
          <Text color="gray.300" fontSize="sm" maxW="320px">
            Your browser blocked the automatic document open. Tap below to open
            it in the native mobile viewer.
          </Text>
          <Button colorPalette="gray" onClick={openDocument}>
            <FiExternalLink />
            Open document
          </Button>
        </Flex>
      </Box>
    </Portal>
  )
}

export default InvestorRelationsMobileDocumentViewer
